import { useNavigate } from "react-router-dom";
import { AlertTriangle, Clock, ArrowRight, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Persistent top-of-page banner that surfaces the trial state.
 *
 * Visibility rules:
 *  - Pro users: hidden.
 *  - Grandfathered users (trial_ends_at === null): hidden.
 *  - Trial ended AND user is a waitlist-region user (INR/PKR): shows a soft
 *    "we'll let you keep editing while we launch local checkout" banner
 *    with a link to the waitlist card on /pricing.
 *  - Trial ended AND user is NOT in a waitlist region: shows a hard "locked"
 *    banner with an "Upgrade" CTA.
 *  - Trial active with <= 7 days remaining: shows a countdown banner.
 *  - Trial active with > 7 days remaining: hidden (sidebar pill covers this).
 *
 * Dismissal:
 *  - Countdown variant supports one-per-day dismissal (localStorage key
 *    `pfo_trial_banner_dismissed_date`). The locked / waitlist variants
 *    are NEVER dismissable — they are a hard state the user must resolve.
 */
export default function TrialBanner() {
  const { trialStatus } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  const days = trialStatus?.days_remaining;
  const isExpired = !!trialStatus?.is_expired;
  const isLocked = !!trialStatus?.is_locked;
  const isWaitlist = !!trialStatus?.is_waitlist_region;
  const isPro = !!trialStatus?.is_pro;
  const hasTrial = !!trialStatus?.trial_ends_at;

  const variant = (() => {
    if (!trialStatus || isPro || !hasTrial) return null;
    if (isLocked) return "locked";
    if (isExpired && isWaitlist) return "waitlist_soft";
    if (!isExpired && typeof days === "number" && days <= 7) return "countdown";
    return null;
  })();

  useEffect(() => {
    if (variant !== "countdown") return;
    try {
      const key = "pfo_trial_banner_dismissed_date";
      const stored = localStorage.getItem(key);
      const today = new Date().toISOString().slice(0, 10);
      setDismissed(stored === today);
    } catch { /* localStorage unavailable */ }
  }, [variant]);

  if (!variant) return null;
  if (variant === "countdown" && dismissed) return null;

  const dismiss = () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      localStorage.setItem("pfo_trial_banner_dismissed_date", today);
    } catch { /* ignore */ }
    setDismissed(true);
  };

  if (variant === "locked") {
    return (
      <div
        className="border-b"
        style={{
          background: "linear-gradient(90deg, rgba(196,91,77,0.10) 0%, rgba(224,158,80,0.12) 100%)",
          borderColor: "rgba(196,91,77,0.35)",
        }}
        data-testid="trial-banner-locked"
      >
        <div className="max-w-6xl mx-auto px-5 lg:px-10 py-3 flex items-center gap-3 flex-wrap">
          <AlertTriangle size={16} style={{ color: "#C45B4D" }} />
          <div className="flex-1 min-w-0 text-sm">
            <span className="font-medium" style={{ color: "var(--text-primary)" }}>
              {t("trial.locked_headline")}
            </span>{" "}
            <span className="text-[var(--text-secondary)]">
              {t("trial.locked_body")}
            </span>
          </div>
          <button
            onClick={() => navigate("/pricing")}
            className="btn-primary text-xs px-3 py-1.5"
            data-testid="trial-banner-upgrade-btn"
          >
            {t("trial.locked_cta")} <ArrowRight size={13}/>
          </button>
        </div>
      </div>
    );
  }

  if (variant === "waitlist_soft") {
    return (
      <div
        className="border-b"
        style={{
          background: "linear-gradient(90deg, rgba(224,158,80,0.08) 0%, rgba(45,90,64,0.06) 100%)",
          borderColor: "rgba(224,158,80,0.30)",
        }}
        data-testid="trial-banner-waitlist"
      >
        <div className="max-w-6xl mx-auto px-5 lg:px-10 py-3 flex items-center gap-3 flex-wrap">
          <Clock size={16} style={{ color: "var(--accent-warm)" }} />
          <div className="flex-1 min-w-0 text-sm">
            <span className="font-medium" style={{ color: "var(--text-primary)" }}>
              {t("trial.waitlist_headline")}
            </span>{" "}
            <span className="text-[var(--text-secondary)]">
              {t("trial.waitlist_body")}
            </span>
          </div>
          <button
            onClick={() => navigate("/pricing")}
            className="btn-ghost text-xs px-3 py-1.5 border border-[var(--accent-warm)]"
            data-testid="trial-banner-waitlist-btn"
            style={{ color: "var(--accent-warm)" }}
          >
            {t("trial.waitlist_cta")} <ArrowRight size={13}/>
          </button>
        </div>
      </div>
    );
  }

  // countdown
  return (
    <div
      className="border-b"
      style={{
        background: "linear-gradient(90deg, rgba(45,90,64,0.05) 0%, rgba(224,158,80,0.08) 100%)",
        borderColor: "rgba(45,90,64,0.20)",
      }}
      data-testid="trial-banner-countdown"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-10 py-2.5 flex items-center gap-3 flex-wrap">
        <Clock size={15} style={{ color: "var(--accent-primary)" }} />
        <div className="flex-1 min-w-0 text-sm">
          <span className="font-medium" style={{ color: "var(--text-primary)" }} data-testid="trial-banner-days-left">
            {t("trial.countdown_headline", { count: days })}
          </span>{" "}
          <span className="text-[var(--text-secondary)]">
            {t("trial.countdown_body")}
          </span>
        </div>
        <button
          onClick={() => navigate("/pricing")}
          className="text-xs font-medium hover:underline"
          style={{ color: "var(--accent-primary)" }}
          data-testid="trial-banner-countdown-cta"
        >
          {t("trial.countdown_cta")} <ArrowRight size={12} className="inline"/>
        </button>
        <button
          onClick={dismiss}
          className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          title={t("trial.countdown_dismiss")}
          aria-label={t("trial.countdown_dismiss")}
          data-testid="trial-banner-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
